import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardSubtitle, CardTitle, Button, usePrompt, useToast } from '@keobook/ui';
import { type LedgerEntry } from '@keobook/shared';
import { api } from '../lib/api';
import { useMoneyFormatter } from '../lib/useCurrency';
import { Layout } from '../components/Layout';
import { AddEntryComposer } from '../components/AddEntryComposer';
import { CoaPicker } from '../components/CoaPicker';

// CONTRACT — DO NOT BREAK:
//   'ai'           — auto-posted by confidence threshold; NO human reviewed it.
//   'staff'        — a CPA explicitly approved this entry. Means "CPA-reviewed".
//   'customer_api' — customer self-posted via composer.
//   'migration'    — imported from prior books.
//   'system'       — synthesized (demo seed, accruals).
// If you change the source stamp on createPostedEntry in apps/api/src/lib/ledger.ts,
// these labels mislead the user. The 2026-06-19 regression mislabeled AI auto-posts
// as 'staff' because the source was a hard-coded default — now it's a required typed
// param. Keep it that way.
const SOURCE_LABEL: Record<string, string> = {
  ai: 'AI',
  staff: 'CPA',
  migration: 'Imported',
  customer_api: 'API',
  system: 'System',
};

type PendingProposal = Awaited<ReturnType<typeof api.getPendingProposals>>['proposals'][number];

export function Activity() {
  const fmt = useMoneyFormatter();
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingProposal[]>([]);
  const [selectedPending, setSelectedPending] = useState<Set<string>>(new Set());
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<null | 'approve' | 'reject' | 'reverse'>(null);
  const prompt = usePrompt();
  const toast = useToast();

  const loadFirst = useCallback(async () => {
    setError(null);
    try {
      const [entriesRes, pendingRes] = await Promise.all([
        // Activity = "needs review". Posted entries shown here are
        // limited to AI auto-posts — every other source ('staff',
        // 'customer_api', 'migration', 'system') was already signed
        // off by a human or the platform and belongs in General Ledger.
        // Per Brickstone feedback 2026-06-22 — see ADR-trail.
        api.getLedgerEntries({ limit: 25, source: 'ai' }),
        api.getPendingProposals().catch(() => ({ proposals: [] })),
      ]);
      setEntries(entriesRes.entries);
      setCursor(entriesRes.nextCursor);
      setPending(pendingRes.proposals);
      // Drop any selections that are no longer in the list.
      setSelectedPending((prev) => {
        const next = new Set<string>();
        for (const p of pendingRes.proposals) if (prev.has(p.id)) next.add(p.id);
        return next;
      });
      setSelectedEntries((prev) => {
        const next = new Set<string>();
        for (const e of entriesRes.entries) if (prev.has(e.id)) next.add(e.id);
        return next;
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load activity.');
    } finally {
      setLoading(false);
    }
  }, []);

  function toggleSelection(id: string) {
    setSelectedPending((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll() {
    setSelectedPending(new Set(pending.map((p) => p.id)));
  }

  function selectNone() {
    setSelectedPending(new Set());
  }

  // One-click "trust the AI for the obvious ones." Picks every pending
  // proposal at >= 95% confidence and selects them so the existing bulk-
  // approve button acts on the batch. Doesn't auto-approve — the CPA still
  // confirms — but cuts the click count from N to 2 (pick + approve).
  function selectHighConfidence() {
    setSelectedPending(new Set(pending.filter((p) => p.confidence >= 0.95).map((p) => p.id)));
  }
  const highConfidenceCount = pending.filter((p) => p.confidence >= 0.95).length;

  async function approveOne(id: string) {
    try {
      await api.approveProposal(id);
      toast({ tone: 'success', message: 'Posted to journal.' });
      void loadFirst();
    } catch (err: unknown) {
      toast({ tone: 'error', message: err instanceof Error ? err.message : 'Could not approve.' });
    }
  }

  async function rejectOne(id: string) {
    const reason = await prompt({
      title: 'Reject proposal',
      body: 'Optional: tell us why. Leave blank to skip.',
      placeholder: 'e.g. duplicate, not a business transaction',
      confirmLabel: 'Reject',
      destructive: true,
    });
    if (reason === null) return;
    try {
      await api.rejectProposal(id, reason || undefined);
      toast({ tone: 'success', message: 'Proposal rejected.' });
      void loadFirst();
    } catch (err: unknown) {
      toast({ tone: 'error', message: err instanceof Error ? err.message : 'Could not reject.' });
    }
  }

  async function bulkApprove() {
    const ids = [...selectedPending];
    if (ids.length === 0) return;
    setBulkBusy('approve');
    try {
      const res = await api.bulkApproveProposals(ids);
      const parts: string[] = [];
      if (res.approved > 0) parts.push(`${res.approved} approved`);
      if (res.skipped > 0) parts.push(`${res.skipped} skipped`);
      if (res.failed > 0) parts.push(`${res.failed} failed`);
      toast({
        tone: res.failed > 0 ? 'error' : 'success',
        message: parts.join(' · '),
      });
      void loadFirst();
    } catch (err: unknown) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Bulk approve failed.',
      });
    } finally {
      setBulkBusy(null);
    }
  }

  async function reverseToReview(entryId: string) {
    // Escape hatch for AI auto-posts the CPA decides need a second look.
    // Server enforces: only source='ai' entries with an originating proposal
    // can be reversed — staff-approved entries stay posted.
    const confirmed = await prompt({
      title: 'Send back to review?',
      body: 'This removes the journal entry and puts the AI proposal back on the review queue so you can edit categorization, then Approve or Reject.',
      placeholder: '',
      confirmLabel: 'Send to review',
      destructive: false,
    });
    if (confirmed === null) return;
    try {
      await api.reverseToReview(entryId);
      toast({ tone: 'success', message: 'Sent back to review queue.' });
      void loadFirst();
    } catch (err: unknown) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Could not reverse entry.',
      });
    }
  }

  function toggleEntry(id: string) {
    setSelectedEntries((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllEntries() {
    setSelectedEntries(new Set(entries.map((e) => e.id)));
  }

  function selectNoEntries() {
    setSelectedEntries(new Set());
  }

  async function bulkReverseToReview() {
    const ids = [...selectedEntries];
    if (ids.length === 0) return;
    const confirmed = await prompt({
      title: `Send ${ids.length} back to review?`,
      body: 'This removes the selected AI journal entries and puts their proposals back on the review queue so you can edit categorization, then Approve or Reject.',
      placeholder: '',
      confirmLabel: 'Send to review',
      destructive: false,
    });
    if (confirmed === null) return;
    setBulkBusy('reverse');
    try {
      const res = await api.bulkReverseToReview(ids);
      const parts: string[] = [];
      if (res.reversed > 0) parts.push(`${res.reversed} sent to review`);
      if (res.skipped > 0) parts.push(`${res.skipped} skipped`);
      if (res.failed > 0) parts.push(`${res.failed} failed`);
      toast({
        tone: res.failed > 0 ? 'error' : 'success',
        message: parts.join(' · ') || 'Nothing to do.',
      });
      setSelectedEntries(new Set());
      void loadFirst();
    } catch (err: unknown) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Bulk send to review failed.',
      });
    } finally {
      setBulkBusy(null);
    }
  }

  async function bulkReject() {
    const ids = [...selectedPending];
    if (ids.length === 0) return;
    const reason = await prompt({
      title: `Reject ${ids.length} proposals`,
      body: 'Optional reason logged on every rejected row. Leave blank to skip.',
      placeholder: 'e.g. duplicate batch from re-uploaded statement',
      confirmLabel: 'Reject',
      destructive: true,
    });
    if (reason === null) return;
    setBulkBusy('reject');
    try {
      const res = await api.bulkRejectProposals(ids, reason || undefined);
      toast({
        tone: 'success',
        message: `${res.rejected} rejected${res.skipped > 0 ? ` · ${res.skipped} skipped` : ''}`,
      });
      void loadFirst();
    } catch (err: unknown) {
      toast({
        tone: 'error',
        message: err instanceof Error ? err.message : 'Bulk reject failed.',
      });
    } finally {
      setBulkBusy(null);
    }
  }

  useEffect(() => {
    void loadFirst();
  }, [loadFirst]);

  async function loadMore() {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      // Same source filter as loadFirst — pagination must stay scoped.
      const res = await api.getLedgerEntries({ limit: 25, cursor, source: 'ai' });
      setEntries((prev) => [...prev, ...res.entries]);
      setCursor(res.nextCursor);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load more.');
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <Layout>
      <div className="kb-page" style={{ maxWidth: 820, margin: '0 auto', padding: '2rem 1.5rem' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
          }}
        >
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 600, color: 'var(--kb-gray-900)', margin: 0 }}>
              Review queue
            </h1>
            <p
              style={{
                fontSize: 13,
                color: 'var(--kb-gray-400)',
                margin: '4px 0 0',
              }}
            >
              Transactions waiting for your judgment — pending AI proposals and AI-posted entries
              you can still pull back. Full posted history lives on{' '}
              <Link to="/ledger" style={{ color: 'var(--kb-brand-green, #3CA960)' }}>
                General Ledger
              </Link>
              .
            </p>
          </div>
        </div>

        <AddEntryComposer onCreated={() => void loadFirst()} />

        {pending.length > 0 && (
          <Card>
            <CardTitle>
              {pending.length} {pending.length === 1 ? 'transaction' : 'transactions'} awaiting your
              review
            </CardTitle>
            <CardSubtitle>
              These came out of the AI categorization with confidence below the auto-post threshold,
              so they stayed as proposals instead of becoming posted entries. Edit the
              categorization if needed, then approve to post — or reject to skip.
            </CardSubtitle>

            {/* Bulk action toolbar */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
                marginTop: 12,
                paddingBottom: 12,
                borderBottom: '1px solid var(--kb-gray-200)',
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedPending.size === pending.length && pending.length > 0}
                  ref={(el) => {
                    // Indeterminate when some-but-not-all are selected.
                    if (el) {
                      el.indeterminate =
                        selectedPending.size > 0 && selectedPending.size < pending.length;
                    }
                  }}
                  onChange={(e) => (e.currentTarget.checked ? selectAll() : selectNone())}
                />
                Select all
              </label>
              <span className="kb-muted" style={{ fontSize: 13 }}>
                {selectedPending.size > 0 ? `${selectedPending.size} selected` : 'none selected'}
              </span>
              {highConfidenceCount > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={selectHighConfidence}
                  title="Select every pending proposal at 95%+ confidence so you can approve them in one click."
                >
                  Select {highConfidenceCount} ≥95%
                </Button>
              )}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={selectedPending.size === 0 || bulkBusy !== null}
                  onClick={() => void bulkApprove()}
                >
                  {bulkBusy === 'approve'
                    ? 'Approving…'
                    : `Approve selected (${selectedPending.size})`}
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  disabled={selectedPending.size === 0 || bulkBusy !== null}
                  onClick={() => void bulkReject()}
                >
                  {bulkBusy === 'reject'
                    ? 'Rejecting…'
                    : `Reject selected (${selectedPending.size})`}
                </Button>
              </div>
            </div>

            {/* Per-row list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
              {pending.map((p) => {
                const debit = p.proposedEntry.lines.find((l) => l.side === 'debit');
                const credit = p.proposedEntry.lines.find((l) => l.side === 'credit');
                const amount = fmt(p.transaction.amount_cents);
                const checked = selectedPending.has(p.id);
                return (
                  <div
                    key={p.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '20px 1fr auto',
                      gap: 10,
                      padding: '12px',
                      border: '1px solid var(--kb-gray-200)',
                      borderRadius: 8,
                      background: checked ? 'var(--kb-brand-50, #ecfdf5)' : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSelection(p.id)}
                      style={{ marginTop: 4 }}
                      aria-label={`Select ${p.transaction.description}`}
                    />
                    <div>
                      <div style={{ fontWeight: 600, color: 'var(--kb-gray-900)' }}>
                        {p.transaction.description}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--kb-gray-400)', marginTop: 2 }}>
                        {p.transaction.date} · {amount} · confidence{' '}
                        {Math.round(p.confidence * 100)}%
                        {p.sourceDocumentFilename ? (
                          <>
                            {' '}
                            · <span>{p.sourceDocumentFilename}</span>
                          </>
                        ) : null}
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '32px 1fr auto',
                          gap: 6,
                          marginTop: 8,
                        }}
                      >
                        <span className="kb-muted" style={{ fontSize: 12 }}>
                          Dr
                        </span>
                        <CoaPicker
                          currentCode={debit?.account_code ?? ''}
                          currentLabel={debit?.account_name}
                          target={{ mode: 'proposal', proposalId: p.id, side: 'debit' }}
                          onDone={() => void loadFirst()}
                        />
                        <span
                          className="kb-report__amt"
                          style={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {amount}
                        </span>
                        <span className="kb-muted" style={{ fontSize: 12 }}>
                          Cr
                        </span>
                        <CoaPicker
                          currentCode={credit?.account_code ?? ''}
                          currentLabel={credit?.account_name}
                          target={{ mode: 'proposal', proposalId: p.id, side: 'credit' }}
                          onDone={() => void loadFirst()}
                        />
                        <span
                          className="kb-report__amt"
                          style={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                          {amount}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={bulkBusy !== null}
                        onClick={() => void approveOne(p.id)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={bulkBusy !== null}
                        onClick={() => void rejectOne(p.id)}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {error && (
          <div role="alert" className="kb-form-error">
            {error}
          </div>
        )}

        {loading ? (
          <Card>
            <CardSubtitle>Loading…</CardSubtitle>
          </Card>
        ) : entries.length === 0 && pending.length === 0 ? (
          <Card>
            <CardTitle>All clear</CardTitle>
            <CardSubtitle>
              Nothing in the review queue. Posted entries (CPA-approved, manual, imported) live on
              the <Link to="/ledger">General Ledger</Link>.
            </CardSubtitle>
          </Card>
        ) : entries.length === 0 ? null : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Bulk send-to-review header — mirrors the pending-proposals bulk bar */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '4px 2px',
                flexWrap: 'wrap',
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedEntries.size === entries.length && entries.length > 0}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate =
                        selectedEntries.size > 0 && selectedEntries.size < entries.length;
                    }
                  }}
                  onChange={(e) =>
                    e.currentTarget.checked ? selectAllEntries() : selectNoEntries()
                  }
                />
                Select all
              </label>
              <span className="kb-muted" style={{ fontSize: 13 }}>
                {selectedEntries.size > 0 ? `${selectedEntries.size} selected` : 'none selected'}
              </span>
              <div style={{ marginLeft: 'auto' }}>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={selectedEntries.size === 0 || bulkBusy !== null}
                  onClick={() => void bulkReverseToReview()}
                >
                  {bulkBusy === 'reverse' ? 'Sending…' : `Send ${selectedEntries.size} to review`}
                </Button>
              </div>
            </div>
            {entries.map((e) => (
              <Card key={e.id}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    alignItems: 'baseline',
                  }}
                >
                  <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                    <input
                      type="checkbox"
                      checked={selectedEntries.has(e.id)}
                      onChange={() => toggleEntry(e.id)}
                      aria-label={`Select ${e.description}`}
                      style={{ marginTop: 2 }}
                    />
                    <div>
                      <div style={{ fontWeight: 600, color: 'var(--kb-gray-900)' }}>
                        {e.description}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--kb-gray-400)', marginTop: 2 }}>
                        {new Date(e.postedAt).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="kb-pill kb-pill--neutral">
                      {SOURCE_LABEL[e.source] ?? e.source}
                    </span>
                    {e.source === 'ai' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => void reverseToReview(e.id)}
                        title="Pull this AI-posted entry back into the review queue so you can approve or reject it."
                      >
                        Send to review
                      </Button>
                    )}
                  </div>
                </div>
                <table className="kb-report" style={{ marginTop: 10 }}>
                  <tbody>
                    {e.lines.map((l, i) => (
                      <tr key={`${e.id}-${i}`}>
                        <td>
                          <CoaPicker
                            currentCode={l.accountCode}
                            currentLabel={l.accountName}
                            target={{ mode: 'ledger', entryId: e.id, lineId: l.id }}
                            onDone={() => void loadFirst()}
                          />
                          {l.memo ? <span className="kb-muted"> — {l.memo}</span> : null}
                        </td>
                        <td className="kb-report__amt">
                          {l.amountCents > 0 ? fmt(l.amountCents) : ''}
                        </td>
                        <td className="kb-report__amt">
                          {l.amountCents < 0 ? fmt(-l.amountCents) : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            ))}
            {cursor && (
              <Button variant="ghost" size="sm" disabled={loadingMore} onClick={loadMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
